import { PlaybackStartupObservation } from '../../model/PlaybackStartupObservation';

const OPERATIONS = new Set<PlaybackStartupObservation['operation']>([
  'playback-authorization',
  'license-authorization',
  'license',
  'manifest',
  'segment',
  'concurrency',
  'ad',
]);
const MILESTONES = new Set<NonNullable<PlaybackStartupObservation['milestone']>>([
  'request-start',
  'response-end',
  'response-error',
  'parsing-start',
  'parsing-end',
  'segment-number',
]);
const NON_PARSING_OPERATIONS = new Set<PlaybackStartupObservation['operation']>(['segment', 'concurrency', 'ad']);

/** Validate JavaScript callers as well as typed integrations. */
export function isPlaybackStartupObservation(value: unknown): value is PlaybackStartupObservation {
  if (!value || typeof value !== 'object') return false;
  const observation = value as PlaybackStartupObservation;
  const { operation, milestone } = observation;
  return (
    OPERATIONS.has(operation) &&
    isValidMilestone(operation, milestone) &&
    hasValidOptionalFields(observation)
  );
}

function isValidMilestone(
  operation: PlaybackStartupObservation['operation'],
  milestone: PlaybackStartupObservation['milestone'],
): boolean {
  if (milestone === undefined) return true;
  if (!MILESTONES.has(milestone)) return false;
  if (milestone === 'segment-number') return operation === 'segment';
  const isParsing = milestone === 'parsing-start' || milestone === 'parsing-end';
  return !isParsing || !NON_PARSING_OPERATIONS.has(operation);
}

function hasValidOptionalFields(observation: PlaybackStartupObservation): boolean {
  const { timestamp, requestId, retryCount, retryFromStart, autoplayAllowed, state } = observation;
  return (
    (timestamp === undefined || (typeof timestamp === 'number' && Number.isFinite(timestamp))) &&
    (requestId === undefined || typeof requestId === 'string') &&
    (retryCount === undefined || (Number.isInteger(retryCount) && retryCount >= 0)) &&
    (retryFromStart === undefined || typeof retryFromStart === 'boolean') &&
    (autoplayAllowed === undefined || typeof autoplayAllowed === 'boolean') &&
    (state === undefined || ['loading', 'buffering', 'ready', 'playing'].includes(state))
  );
}
